import { darken, lighten } from 'polished'
import React, { useMemo } from 'react'
import { Activity } from 'react-feather'
import styled, { keyframes } from 'styled-components'

import useENSName from '../../hooks/useENSName'
import { useWalletModalToggle } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/reducer'
import { shortenAddress } from '../../utils'
import { ButtonSecondary } from '../Button'

import Loader from '../Loader'

import { RowBetween } from '../Row'
import { ChainId } from '@hyperswap/addresses'
import { useActiveWeb3React } from 'hooks'
import { useLogin } from '@privy-io/react-auth'
import WalletModal from 'components/WalletModal'
import { useHLNames } from 'hooks/useHLNames'
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  } 
`
const Web3StatusGeneric = styled(ButtonSecondary)`
  animation: ${fadeIn} 0.3s ease;
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  :focus {
    outline: none;
  }
`
const Web3StatusError = styled(Web3StatusGeneric)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  animation: ${fadeIn} 0.3s ease;
  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
`

const Web3StatusConnect = styled(Web3StatusGeneric)<{ faded?: boolean }>`
  animation: ${fadeIn} 0.3s ease;
  background-color: ${({ theme }) => theme.primary1};
  border: 1px solid #4afb39;
  color: #090524;
  font-weight: 500;
  border-radius: 10px;

  :hover,
  :focus {
    border: 1px solid #4afb38;
    color: #090524;
  }
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{ pending?: boolean }>`
  animation: ${fadeIn} 0.3s ease;
  background-color: ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg2)};
  border: 1px solid ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg2)};
  color: ${({ pending, theme }) => (pending ? theme.white : theme.text1)};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ pending, theme }) => (pending ? darken(0.05, theme.primary1) : lighten(0.05, theme.bg1))};

    :focus {
      border: 1px solid ${({ pending, theme }) => (pending ? darken(0.1, theme.primary1) : darken(0.1, theme.bg2))};
    }
  }
`

const NetworkIcon = styled(Activity)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

const Text = styled.p`
  animation: ${fadeIn} 0.3s ease;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem 0 0.25rem;
  font-size: 14px;
  width: fit-content;
  font-weight: 400;
`

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

function Web3StatusInner() {
  const { account, wallet, wrongChain, walletsReady, privyReady } = useActiveWeb3React()
  const { login } = useLogin()
  const toggleWalletModal = useWalletModalToggle()
  const { name: hlName, loading: hlNameLoading } = useHLNames(account)
  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)
  const hasPendingTransactions = !!pending.length
  const isReady = walletsReady && privyReady

  if (!wallet) {
    return (
      <Web3StatusConnect
        id="connect-wallet"
        onClick={() =>
          login({
            loginMethods: ['wallet'],
            walletChainType: 'ethereum-only',
            disableSignup: false,
          })
        }
        style={{ height: '30px' }}
      >
        {isReady ? <Text>Connect Wallet</Text> : <Loader stroke="#090524" />}
      </Web3StatusConnect>
    )
  } else if (account && !wrongChain) {
    return (
      <Web3StatusConnected
        id="web3-status-connected"
        pending={hasPendingTransactions}
        onClick={() => {
          toggleWalletModal()
        }}
      >
        {hasPendingTransactions ? (
          <RowBetween>
            <Text style={{ color: '#090524' }}>{pending?.length} Pending</Text> <Loader stroke="#090524" />
          </RowBetween>
        ) : (
          <>
            <Text style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {hlName && hlNameLoading === false ? hlName : shortenAddress(account, 2)}{' '}
              {wallet?.meta?.icon && (
                <img style={{ width: '18px', height: '18px' }} src={wallet?.meta?.icon} alt="logo wallet" />
              )}
            </Text>
          </>
        )}
      </Web3StatusConnected>
    )
  } else if (wrongChain) {
    return (
      <Web3StatusError onClick={() => wallet?.switchChain(ChainId.HYPEREVM)}>
        <NetworkIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="#FF6871"
            stroke="#FF6871"
            strokeWidth="1"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path
              d="M3 17h2.397a5 5 0 0 0 4.096 -2.133l.177 -.253m3.66 -5.227l.177 -.254a5 5 0 0 1 4.096 -2.133h3.397"
              fill="#FF6871"
            />
            <path d="M18 4l3 3l-3 3" fill="#FF6871" />
            <path d="M3 7h2.397a5 5 0 0 1 4.096 2.133l4.014 5.734a5 5 0 0 0 4.096 2.133h3.397" fill="#FF6871" />
            <path d="M18 20l3 -3l-3 -3" fill="#FF6871" />
          </svg>
        </NetworkIcon>
        <Text>Wrong Network</Text>
      </Web3StatusError>
    )
  } else {
    return (
      <Web3StatusConnect
        id="connect-wallet"
        onClick={() =>
          login({
            loginMethods: ['wallet'],
            walletChainType: 'ethereum-only',
            disableSignup: false,
          })
        }
      >
        <Text>Connect to a wallet</Text>
      </Web3StatusConnect>
    )
  }
}

export default function Web3Status() {
  const { account } = useActiveWeb3React()

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)
  const confirmed = sortedRecentTransactions.filter((tx) => tx.receipt).map((tx) => tx.hash)

  /*  if (!contextNetwork.active && !active) {
    return null
  } */

  return (
    <>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
